// Neutral panel tones used by every illustration
const BASE = "var(--color-neutral-base)";
const SOFT = "var(--color-neutral-light)";
const STRONG = "var(--color-neutral-150)";

const Lines = ({ x, y, widths, gap = 12 }: { x: number; y: number; widths: number[]; gap?: number }) => (
  <>
    {widths.map((width, index) => (
      <rect key={index} x={x} y={y + index * gap} width={width} height={5} rx={2.5} fill={BASE} />
    ))}
  </>
);

const illustrations: Record<string, React.ReactNode> = {
  welcome: (
    <>
      <circle cx={80} cy={34} r={15} fill={SOFT} />
      <rect x={48} y={58} width={64} height={8} rx={4} fill={STRONG} />
      <rect x={60} y={72} width={40} height={5} rx={2.5} fill={BASE} />
    </>
  ),
  about: (
    <>
      <rect x={24} y={18} width={72} height={9} rx={4.5} fill={STRONG} />
      <Lines x={24} y={38} widths={[112, 104, 112, 76]} />
    </>
  ),
  form: (
    <>
      {[14, 34, 54].map(y => (
        <rect key={y} x={24} y={y} width={112} height={14} rx={4} fill="none" stroke={SOFT} strokeWidth={1.5} />
      ))}
      <rect x={24} y={76} width={112} height={13} rx={6.5} fill={STRONG} />
    </>
  ),
  catalog: (
    <>
      {[16, 62, 108].map(x => (
        <g key={x}>
          <rect x={x} y={16} width={38} height={68} rx={6} fill={BASE} />
          <rect x={x + 4} y={20} width={30} height={32} rx={4} fill={SOFT} />
          <rect x={x + 4} y={58} width={24} height={5} rx={2.5} fill={STRONG} />
          <rect x={x + 4} y={68} width={16} height={5} rx={2.5} fill={SOFT} />
        </g>
      ))}
    </>
  ),
  button: (
    <>
      <rect x={36} y={38} width={88} height={24} rx={12} fill={STRONG} />
      <rect x={58} y={47.5} width={44} height={5} rx={2.5} fill={BASE} />
    </>
  ),
  "floating-whatsapp": (
    <>
      <Lines x={20} y={16} widths={[96, 120, 88, 110, 70]} gap={11} />
      <circle cx={130} cy={76} r={14} fill={STRONG} />
      <path d="M124 82l1.6-4.4A7 7 0 1 1 128 80z" fill="none" stroke={BASE} strokeWidth={2} strokeLinejoin="round" />
    </>
  ),
  links: (
    <>
      {[12, 32, 52, 72].map(y => (
        <rect key={y} x={24} y={y} width={112} height={15} rx={7.5} fill={y === 12 ? STRONG : BASE} />
      ))}
    </>
  ),
  social: (
    <>
      {[36, 58, 80, 102, 124].map(cx => (
        <circle key={cx} cx={cx} cy={50} r={9} fill={cx === 80 ? STRONG : SOFT} />
      ))}
    </>
  ),
  location: (
    <>
      <rect x={20} y={14} width={120} height={72} rx={8} fill={BASE} />
      <path d="M20 52h120M64 14v72M104 14v72M20 34l120 30" stroke={SOFT} strokeWidth={1.5} opacity={0.6} />
      <path d="M84 30c-7 0-12 5-12 11 0 9 12 20 12 20s12-11 12-20c0-6-5-11-12-11z" fill={STRONG} />
      <circle cx={84} cy={41} r={4} fill={BASE} />
    </>
  ),
  "media.image": (
    <>
      <rect x={24} y={14} width={112} height={72} rx={8} fill={BASE} />
      <circle cx={112} cy={34} r={8} fill={SOFT} />
      <path d="M24 78l30-30 22 22 16-14 44 30v0a8 8 0 0 1-8 8H32a8 8 0 0 1-8-8z" fill={STRONG} />
    </>
  ),
  "media.video": (
    <>
      <rect x={24} y={14} width={112} height={72} rx={8} fill={BASE} />
      <circle cx={80} cy={50} r={16} fill={STRONG} />
      <path d="M75 42v16l13-8z" fill={BASE} />
    </>
  ),
  "media.audio": (
    <>
      <rect x={16} y={32} width={128} height={36} rx={18} fill={BASE} />
      <circle cx={36} cy={50} r={11} fill={STRONG} />
      <path d="M33 44v12l9-6z" fill={BASE} />
      {[56, 64, 72, 80, 88, 96, 104, 112, 120, 128].map((x, index) => {
        const height = [8, 16, 10, 20, 12, 18, 8, 14, 10, 6][index];
        return <rect key={x} x={x} y={50 - height / 2} width={4} height={height} rx={2} fill={SOFT} />;
      })}
    </>
  ),
  faq: (
    <>
      {[14, 34, 54, 74].map(y => (
        <g key={y}>
          <rect x={20} y={y} width={120} height={14} rx={4} fill={BASE} />
          <rect x={28} y={y + 4.5} width={60} height={5} rx={2.5} fill={SOFT} />
          <path d={`M128 ${y + 7}h6M131 ${y + 4}v6`} stroke={STRONG} strokeWidth={1.8} strokeLinecap="round" />
        </g>
      ))}
    </>
  ),
  highlights: (
    <>
      {[12, 72, 132].map(x => (
        <g key={x}>
          <rect x={x} y={18} width={52} height={64} rx={6} fill={BASE} />
          <rect x={x + 4} y={22} width={44} height={34} rx={4} fill={SOFT} />
          <rect x={x + 4} y={62} width={32} height={5} rx={2.5} fill={STRONG} />
        </g>
      ))}
    </>
  ),
  testimonials: (
    <>
      {[52, 66, 80, 94, 108].map(cx => (
        <circle key={cx} cx={cx} cy={18} r={4} fill={STRONG} />
      ))}
      <Lines x={32} y={32} widths={[96, 96]} gap={10} />
      <rect x={52} y={52} width={56} height={5} rx={2.5} fill={BASE} />
      <circle cx={80} cy={72} r={9} fill={SOFT} />
      <rect x={62} y={86} width={36} height={5} rx={2.5} fill={STRONG} />
    </>
  ),
  pricing: (
    <>
      <rect x={36} y={8} width={88} height={86} rx={8} fill={BASE} />
      <rect x={46} y={16} width={12} height={12} rx={3} fill={SOFT} />
      <rect x={46} y={34} width={40} height={5} rx={2.5} fill={SOFT} />
      <rect x={46} y={44} width={52} height={10} rx={3} fill={STRONG} />
      <rect x={46} y={62} width={60} height={4} rx={2} fill={SOFT} />
      <rect x={46} y={70} width={52} height={4} rx={2} fill={SOFT} />
      <rect x={46} y={80} width={68} height={8} rx={4} fill={STRONG} />
    </>
  ),
};

type Props = {
  type: string;
  subType?: string;
};

/** Neutral-toned mini illustration of how a section looks on the published Keepo. */
export const SectionPreview = ({ type, subType }: Props) => (
  <svg viewBox="0 0 160 100" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    {illustrations[subType ? `${type}.${subType}` : type]}
  </svg>
);
